/**
 * Mobile install + fullscreen helpers.
 *
 *   beforeinstallprompt        — captured at module load, replayed by
 *                                triggerInstall() inside a user gesture.
 *                                Android Chrome only; iOS Safari ignores
 *                                it (no programmatic install on iOS).
 *
 *   requestFullscreen          — only works inside a user gesture chain
 *                                AND on browsers that implement the API.
 *                                iOS uses its normal browser viewport or
 *                                installed PWA: native fullscreen can expose
 *                                a larger layout than the visible touch area.
 *
 * UI consumers should:
 *   - hide install/fullscreen surfaces when isStandalone() (the user
 *     already got what we'd be nagging them about);
 *   - prefer canInstallNow() over checking the event listener directly;
 *   - call tryEnterFullscreen() SYNCHRONOUSLY inside a click handler,
 *     since the browser checks for gesture freshness.
 */

/** Subset of the BeforeInstallPromptEvent we actually use. Not in
 *  lib.dom.d.ts so we declare the shape here. */
interface InstallPromptEvent extends Event {
  readonly platforms: ReadonlyArray<string>;
  readonly userChoice: Promise<{ outcome: 'accepted' | 'dismissed'; platform: string }>;
  prompt(): Promise<void>;
}

let deferredInstallPrompt: InstallPromptEvent | null = null;

if (typeof window !== 'undefined') {
  window.addEventListener('beforeinstallprompt', (e) => {
    // Default behaviour shows the install mini-infobar; we'd rather
    // trigger it from our own button so the timing matches the player's
    // attention rather than the browser's heuristic.
    e.preventDefault();
    deferredInstallPrompt = e as InstallPromptEvent;
  });
  // Clear the cached prompt once the user actually installs — prevents
  // a stale prompt sitting around after the PWA is on the home screen.
  window.addEventListener('appinstalled', () => {
    deferredInstallPrompt = null;
  });
}

/** True iff the page is running as an installed PWA (standalone window). */
export function isStandalone(): boolean {
  if (typeof window === 'undefined') return false;
  if (window.matchMedia && window.matchMedia('(display-mode: standalone)').matches) return true;
  // iOS Safari exposes a navigator.standalone flag (not in TS lib).
  const navStandalone = (window.navigator as Navigator & { standalone?: boolean }).standalone;
  return navStandalone === true;
}

/** True iff this is iOS Safari specifically. iOS Chrome / Firefox /
 *  Edge use WKWebView under the hood and don't expose Add-to-Home-Screen
 *  the same way, so we tailor the hint copy. */
export function isIosSafari(): boolean {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent;
  const isIos = /iPad|iPhone|iPod/.test(ua) || (ua.includes('Mac') && 'ontouchend' in document);
  if (!isIos) return false;
  const isSafari = /Safari\//.test(ua) && !/CriOS|FxiOS|EdgiOS|OPiOS/.test(ua);
  return isSafari;
}

/** Heuristic — used to gate the install/fullscreen chip so desktop
 *  visitors don't see install nags they can't act on usefully. */
export function isMobileViewport(): boolean {
  if (typeof window === 'undefined') return false;
  // Touch-capable + narrow viewport. matchMedia rules out big touchscreens
  // (e.g. Surface Studio) where the install hint would be confusing.
  const touch = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
  const narrow = window.matchMedia('(max-width: 900px)').matches;
  return touch && narrow;
}

/** Desktop macOS fullscreen activates Game Mode / Game Overlay, and the OS can
 *  claim wireless-controller buttons before the browser sees them. iPadOS can
 *  present a Mac-like UA, so require no touch points before calling it desktop. */
function isDesktopMac(): boolean {
  if (typeof navigator === 'undefined') return false;
  return /Macintosh|Mac OS X/.test(navigator.userAgent) && (navigator.maxTouchPoints ?? 0) === 0;
}

export function canInstallNow(): boolean {
  return deferredInstallPrompt !== null;
}

/** Replay the captured install prompt. Must be called inside a user
 *  gesture (button click). Returns true if the user accepted. */
export async function triggerInstall(): Promise<boolean> {
  const ev = deferredInstallPrompt;
  if (!ev) return false;
  await ev.prompt();
  const choice = await ev.userChoice;
  if (choice.outcome === 'accepted') {
    deferredInstallPrompt = null;
    return true;
  }
  return false;
}

/** Call from a click handler. Fire-and-forget — we don't await because
 *  awaiting can break the gesture chain on some browsers, and the
 *  promise's resolution isn't actionable for us anyway. Silently no-ops
 *  when the API is missing. iOS keeps the browser's usable viewport. */
export function tryEnterFullscreen(): void {
  if (typeof document === 'undefined') return;
  // On desktop macOS, browser fullscreen can hand the session to macOS Game
  // Mode/Game Overlay. That overlay may bind controller buttons such as Home /
  // Guide and feel like the OS has "grabbed" the pad, so don't auto-enter it.
  // On the physical iPhone, native fullscreen reported 812px of layout but
  // clipped the visible controls at about 678px. API presence is not enough:
  // keep Safari browser play inside its measured viewport. Installed PWAs
  // already have their own fullscreen surface and need no transition.
  if (isDesktopMac() || isIosSafari() || isStandalone()) return;
  const docEl = document.documentElement as HTMLElement & {
    webkitRequestFullscreen?: () => Promise<void>;
  };
  const d = document as Document & { webkitFullscreenElement?: Element };
  if (d.fullscreenElement || d.webkitFullscreenElement) return;
  // Standard first, webkit-prefixed for Safari fallback.
  if (docEl.requestFullscreen) {
    docEl.requestFullscreen().catch(() => { /* user rejected / blocked */ });
  } else if (docEl.webkitRequestFullscreen) {
    try { docEl.webkitRequestFullscreen(); } catch { /* ignore */ }
  }
}

/** True iff requestFullscreen is callable. Use to decide whether to
 *  show a "TAP FOR FULLSCREEN" button at all. */
export function hasFullscreenAPI(): boolean {
  if (typeof document === 'undefined') return false;
  const docEl = document.documentElement as HTMLElement & {
    webkitRequestFullscreen?: () => Promise<void>;
  };
  return typeof docEl.requestFullscreen === 'function'
      || typeof docEl.webkitRequestFullscreen === 'function';
}

const INSTALL_HINT_DISMISSED_KEY = 'pallasite:installHintDismissed';

export function isInstallHintDismissed(): boolean {
  if (typeof localStorage === 'undefined') return false;
  try { return localStorage.getItem(INSTALL_HINT_DISMISSED_KEY) === '1'; }
  catch { return false; }
}

export function dismissInstallHint(): void {
  if (typeof localStorage === 'undefined') return;
  try { localStorage.setItem(INSTALL_HINT_DISMISSED_KEY, '1'); }
  catch { /* ignore */ }
}
